import React, { useState, useEffect } from 'react';
import { StoreOffer } from '../../api/StoreOffersAPI';
import StoreOfferForm from '../StoreOfferForm/StoreOfferForm';
import { isValidText } from '../../shared/validation';

type Props = {
  offer: StoreOffer;
  onChange: (offer: StoreOffer, errors: Record<string, string>) => void;
  isDisabled?: boolean;
  isCreating?: boolean;
  onStepChange?: (step: number, stepTitle: string, handlers: { handleNext: () => void; handlePrevious: () => void; canProceed: () => boolean }) => void;
};

type Step = {
  id: number;
  title: string;
  key: string;
};

const STEPS: Step[] = [
  { id: 1, title: 'Basic Information', key: 'basic' },
  { id: 2, title: 'Pricing', key: 'pricing' },
  { id: 3, title: 'Availability', key: 'availability' },
  { id: 4, title: 'Gown Details', key: 'gown' },
  { id: 5, title: 'Images', key: 'images' },
  { id: 6, title: 'Additional Details', key: 'details' },
];

function StoreOfferFormSteps({ offer, onChange, isDisabled = false, isCreating = false, onStepChange }: Props) {
  const isEditing = !!offer?.id;
  const [currentStep, setCurrentStep] = useState(1);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Reset to step 1 when offer changes (new offer created)
  useEffect(() => {
    if (!isEditing && offer && !offer.id) {
      setCurrentStep(1);
      setFormErrors({});
    }
  }, [isEditing, offer]);

  // Notify parent of step changes
  useEffect(() => {
    if (onStepChange && !isEditing) {
      const currentStepData = STEPS.find(step => step.id === currentStep);
      const stepTitle = currentStepData ? currentStepData.title : `Step ${currentStep}`;
      onStepChange(currentStep, stepTitle, { handleNext, handlePrevious, canProceed });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, isEditing]);

  // Safety check - ensure offer exists (after hooks)
  if (!offer) {
    return <div className="p-4 text-gray-500">Loading form...</div>;
  }

  // If editing, show all sections at once (no steps)
  if (isEditing) {
    return <StoreOfferForm offer={offer} onChange={onChange} isDisabled={isDisabled} isCreating={false} />;
  }

  const validateStep = (step: number): boolean => {
    if (!offer) return false;
    
    const errors: Record<string, string> = {};

    switch (step) {
      case 1: // Basic Info
        if (!offer.name || !isValidText(offer.name, 3)) {
          errors.name = 'Name must be at least 3 characters';
        }
        break;
      case 2: // Pricing
        if (offer.price === undefined || offer.price < 0) {
          errors.price = 'Price must be 0 or greater';
        }
        if (offer.onSale && offer.salePrice !== undefined) {
          if (offer.salePrice < 0) {
            errors.salePrice = 'Sale price must be 0 or greater';
          }
          if (offer.price !== undefined && offer.salePrice >= offer.price) {
            errors.salePrice = 'Sale price must be lower than regular price';
          }
        }
        break;
      case 3: // Availability
        if (offer.available && offer.quantity !== undefined && offer.quantity < 0) {
          errors.quantity = 'Quantity must be 0 or greater';
        }
        break;
      case 4: // Gown Details
        // Gown details are mostly optional, but we can add validation if needed
        break;
      case 5: // Images
        // Images are optional
        break;
    }

    // Don't set state here - just return validation result
    return Object.keys(errors).length === 0;
  };

  const handleStepChange = (newStep: number) => {
    if (newStep < 1 || newStep > STEPS.length) return;
    
    // Validate current step before moving
    if (newStep > currentStep && !validateStep(currentStep)) {
      // Update errors for display
      const errors: Record<string, string> = {};
      switch (currentStep) {
        case 1:
          if (!offer.name || !isValidText(offer.name, 3)) {
            errors.name = 'Name must be at least 3 characters';
          }
          break;
        case 2:
          if (offer.price === undefined || offer.price < 0) {
            errors.price = 'Price must be 0 or greater';
          }
          break;
        case 3:
          if (offer.available && offer.quantity !== undefined && offer.quantity < 0) {
            errors.quantity = 'Quantity must be 0 or greater';
          }
          break;
      }
      setFormErrors(errors);
      return;
    }

    setCurrentStep(newStep);
    setFormErrors({}); // Clear errors when moving to a new step
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      handleStepChange(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      handleStepChange(currentStep - 1);
    }
  };

  const handleFormChange = (updatedOffer: StoreOffer, errors: Record<string, string>) => {
    setFormErrors(errors);
    
    onChange(updatedOffer, errors);
  };

  const renderStepContent = () => {
    try {
      const sectionMap: Record<number, 'basic' | 'pricing' | 'availability' | 'gown' | 'images' | 'details'> = {
        1: 'basic',
        2: 'pricing',
        3: 'availability',
        4: 'gown',
        5: 'images',
        6: 'details',
      };

      const section = sectionMap[currentStep];
      if (!section) return <div className="p-4 text-red-500">Invalid step</div>;

      return (
        <StoreOfferForm 
          offer={offer} 
          onChange={handleFormChange} 
          isDisabled={isDisabled}
          showOnlySection={section}
          isCreating={isCreating}
        />
      );
    } catch (error) {
      console.error('Error rendering step content:', error);
      return <div className="p-4 text-red-500">Error loading form step</div>;
    }
  };


  const canProceed = (): boolean => {
    // Use memoized validation without side effects
    if (!offer) return false;
    
    switch (currentStep) {
      case 1:
        return !!(offer.name && isValidText(offer.name, 3));
      case 2:
        return offer.price !== undefined && offer.price >= 0;
      case 3:
        return !(offer.available && offer.quantity !== undefined && offer.quantity < 0);
      case 4:
      case 5:
      case 6:
        return true; // Optional steps
      default:
        return false;
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Content */}
      <div className="min-h-[400px] transition-all duration-300">
        {renderStepContent()}
      </div>

      {/* Error Display */}
      {Object.keys(formErrors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm font-medium text-red-800 mb-1">
            Please fix the following errors:
          </p>
          <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
            {Object.values(formErrors).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default StoreOfferFormSteps;

